import { useEffect, useId, useState } from "react";
import type { TaskId, TaskView } from "../api/generated";
import type { TaskApi } from "../api/taskApi";

const MAX_SUGGESTIONS = 6;

interface DependencyFieldProps {
  api: Pick<TaskApi, "get" | "query">;
  taskId: TaskId;
  dependencies: TaskId[];
  blockedBy: TaskId[];
  revision: unknown;
  onAdd(id: TaskId): void;
  onRemove(id: TaskId): void;
}

export function DependencyField({
  api,
  taskId,
  dependencies,
  blockedBy,
  revision,
  onAdd,
  onRemove,
}: DependencyFieldProps) {
  const inputId = useId();
  const [titles, setTitles] = useState<Map<TaskId, string>>(new Map());
  const [search, setSearch] = useState("");
  const [suggestions, setSuggestions] = useState<TaskView[]>([]);

  useEffect(() => {
    let cancelled = false;
    void Promise.all(dependencies.map((id) => api.get(id))).then((views) => {
      if (cancelled) return;
      setTitles(new Map(views.flatMap((view) => (view === undefined ? [] : [[view.task.id, view.task.title] as const]))));
    });
    return () => {
      cancelled = true;
    };
  }, [api, dependencies, revision]);

  useEffect(() => {
    const text = search.trim();
    if (text === "") return;
    let cancelled = false;
    const quoted = `"${text.replaceAll("\\", "\\\\").replaceAll('"', '\\"')}"`;
    void api.query(quoted, "Urgency").then(
      (found) => {
        if (cancelled) return;
        const eligible = found.filter(
          (view) => view.task.id !== taskId && !dependencies.includes(view.task.id),
        );
        setSuggestions(eligible.slice(0, MAX_SUGGESTIONS));
      },
      () => setSuggestions([]),
    );
    return () => {
      cancelled = true;
    };
  }, [api, search, taskId, dependencies]);

  return (
    <div className="field">
      <label htmlFor={inputId}>Depends on</label>
      <ul className="task-list">
        {dependencies.map((id) => (
          <li key={id} className="dependency-option">
            <span style={{ flex: 1 }}>
              {titles.get(id) ?? "(unavailable task)"}
              {blockedBy.includes(id) && <span className="meta-soon"> · open</span>}
            </span>
            <button
              type="button"
              className="icon-button"
              aria-label={`Remove dependency ${titles.get(id) ?? id}`}
              onClick={() => onRemove(id)}
            >
              ×
            </button>
          </li>
        ))}
      </ul>
      <input
        id={inputId}
        className="text-input"
        value={search}
        autoComplete="off"
        placeholder="Search for a task to depend on"
        onChange={(event) => setSearch(event.target.value)}
      />
      {search.trim() !== "" && suggestions.length > 0 && (
        <ul className="suggestions" aria-label="Matching tasks">
          {suggestions.map((view) => (
            <li key={view.task.id}>
              <button
                type="button"
                onClick={() => {
                  onAdd(view.task.id);
                  setSearch("");
                }}
              >
                {view.task.title}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
